import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// Animated Chatbot Attention Bubble
export const ChatbotAttentionBubble = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('chatbot_bubble_dismissed')) {
      setDismissed(true);
      return;
    }
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Auto-hide after 12 seconds
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => {
      setVisible(false);
      setDismissed(true);
      sessionStorage.setItem('chatbot_bubble_dismissed', '1');
    }, 12000);
    return () => clearTimeout(timer);
  }, [visible]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    sessionStorage.setItem('chatbot_bubble_dismissed', '1');
  };

  // Mirror ChatbotWidget visibility: no bubble for workers, or for admins inside the dashboard (chatbot is hidden there)
  if (user?.role === 'worker') return null;
  if (user?.role && ['admin', 'god_admin'].includes(user.role) && location.pathname.startsWith('/dashboard')) return null;
  if (location.pathname.startsWith('/q/') || location.pathname.startsWith('/i/')) return null;
  if (['/login', '/register', '/forgot-password'].some((p) => location.pathname.startsWith(p))) return null;
  if (dismissed || !visible) return null;

  return (
    <div
      data-testid="chatbot-attention-bubble"
      className="fixed z-[46] pointer-events-auto"
      style={{ bottom: '210px', right: '20px' }}
    >
      {/* Speech bubble */}
      <div
        onClick={handleDismiss}
        className="cursor-pointer"
        style={{
          animation: 'chatBubbleEntrance 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
          willChange: 'transform, opacity',
        }}
      >
        <div
          className="relative bg-white rounded-2xl pl-4 pr-7 py-3 shadow-2xl"
          style={{
            maxWidth: '220px',
            filter: 'drop-shadow(0 8px 24px rgba(0, 153, 255, 0.3))',
          }}
        >
          {/* Close button */}
          <button
            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
            className="no-min-touch absolute -top-2 -right-2 w-6 h-6 bg-[#0A0A0A] rounded-full flex items-center justify-center text-white hover:bg-red-500 transition-colors"
            style={{ fontSize: '10px' }}
            aria-label="Close chat bubble"
          >
            <X size={12} />
          </button>

          {/* Waving hand + text */}
          <div className="flex items-center gap-2">
            <span
              className="text-2xl inline-block"
              style={{ animation: 'waveHand 1.5s ease-in-out infinite' }}
            >
              {'\u{1F44B}'}
            </span>
            <div>
              <p className="text-[#0A0A0A] text-sm font-bold leading-tight">
                Need help?
              </p>
              <p className="text-[#666] text-xs leading-tight">
                Chat with us!
              </p>
            </div>
          </div>

          {/* Arrow pointing down to chatbot icon */}
          <div
            className="absolute -bottom-3 right-4"
            style={{
              width: 0,
              height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '12px solid white',
            }}
          />
        </div>

        {/* Animated dots trail pointing to chatbot */}
        <div className="flex justify-end pr-4 mt-1 gap-1">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="rounded-full bg-[#0099FF]"
              style={{
                width: `${6 - i * 1.5}px`,
                height: `${6 - i * 1.5}px`,
                opacity: 1 - i * 0.25,
                animation: `dotPulse 1.5s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes chatBubbleEntrance {
          0% { opacity: 0; transform: scale(0.3) translateY(20px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes chatBubbleFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes waveHand {
          0%, 100% { transform: rotate(0deg); }
          15% { transform: rotate(14deg); }
          30% { transform: rotate(-8deg); }
          45% { transform: rotate(14deg); }
          60% { transform: rotate(-4deg); }
          75% { transform: rotate(10deg); }
          90% { transform: rotate(0deg); }
        }
        @keyframes dotPulse {
          0%, 100% { opacity: 0.4; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
};
